import {
  PathCreatorResourceFragment,
  usePathCreatorResourcesQuery
} from "@pathwright/graphql-client/src/generated"
import { useMemo } from "react"
import PathObjectSelector, {
  PathObjectSelectorProps
} from "./PathObjectSelector"
import { registrationRoleIntMap } from "./RoleBadge"
import { SelectedResource } from "./state"
import useRandomThemeResourceImage from "./useRandomThemeResourceImage"
import { flattenEdges } from "@pathwright/utils"

// Owned resources have a local source cohort. Store-acquired resources usually
// do not; Path Creator includes those when the school has a StoreLink to the
// licensor.
export type SelectedResourceWithSourceCohort = PathCreatorResourceFragment & {
  source_cohort: NonNullable<PathCreatorResourceFragment["source_cohort"]>
}

function isStoreAcquiredResource(
  resource: PathCreatorResourceFragment,
  storeLinkLicensorIds: Set<number>
) {
  return (
    !!resource.licensed_from_resource_id &&
    storeLinkLicensorIds.has(resource.licensed_from_school_id || -1)
  )
}

// Maps the PathCreatorResourceFragment to the SelectedResource type.
export function massageResource(
  resource: PathCreatorResourceFragment
): SelectedResource {
  const role = (registrationRoleIntMap.get(
    resource.source_cohort?.registration?.role!
  )?.enum ?? null) as SelectedResource["role"]

  return {
    id: resource.id,
    name: resource.name,
    image: resource.image,
    background_image: resource.background_image,
    role,
    ...(resource.source_cohort
      ? {
          sourceCohort: {
            id: resource.source_cohort.id,
            role
          }
        }
      : {})
  }
}

type ResourceSelectorProps = Omit<
  PathObjectSelectorProps<SelectedResource>,
  "options" | "isLoading" | "itemLabel" | "getNewOptionData" | "kind"
>

function ResourceSelector({
  value,
  onChange,
  ...itemSelectorProps
}: ResourceSelectorProps) {
  // cache-and-network so Select existing picks up Paths licensed earlier in this session.
  const query = usePathCreatorResourcesQuery({
    fetchPolicy: "cache-and-network"
  })
  const storeLinkLicensorIds = useMemo(() => {
    const storeLinksIds = (flattenEdges(query.data?.storeLinks) || []).map((storeLink) => storeLink.store?.licensor?.id)
    return new Set(storeLinksIds)
  }, [query.data?.storeLinks])

  const resources =
    query.data?.resources?.edges
      ?.map((edge) => edge?.node!)
      .reduce((acc, node) => {
        if (
          node.source_cohort ||
          isStoreAcquiredResource(node, storeLinkLicensorIds)
        ) {
          acc.push(massageResource(node))
        }
        return acc
      }, [] as SelectedResource[]) || []

  // Selecting resource when that is the status or we
  // have a selected resource.
  const [defaultImage, image, burredImage] = useRandomThemeResourceImage()

  // "Create a new Path"
  const createOption = {
    id: -1,
    name: "",
    image: image,
    background_image: burredImage,
    sourceCohort: {
      id: -1,
      role: null
    }
  }

  return (
    <PathObjectSelector
      itemLabel="Path"
      icon={"path"}
      kind="resource"
      options={resources}
      value={value?.id ? value : null}
      onChange={onChange}
      isLoading={query.loading}
      getNewOptionData={(inputValue, optionLabel) => ({
        ...createOption,
        name: inputValue
      })}
      // We blanketly deny creating a new resource using the select menu.
      isValidNewOption={() => false}
      {...itemSelectorProps}
    />
  )
}

export default ResourceSelector
